/**
 * AtomDefaultsContext — theme-driven per-atom default props for renderers.
 *
 * Populated by AppV2 from `theme.atomProps` (ResolvedTheme in @cogo/design-presets).
 * Consumed by element renderers (button, input, select, …) to fill in missing
 * variant / size / radius / shadow / padding when the block JSON leaves them
 * unspecified. See docs/design-system/theme-v2-design-2026-04-19.md.
 *
 * Lives in @cogo/react-renderer (v1) so in-tree renderers can consume it
 * without a circular dep on @cogo/react-renderer-v2. v2 re-exports the same
 * Provider + hook so existing import sites remain valid.
 *
 * The `AtomDefaults` shape is kept inline (not imported from @cogo/design-presets)
 * to avoid adding a new workspace dep to v1 for a seven-field data interface.
 * The canonical typed version lives in @cogo/design-presets/theme-v2-types —
 * these two definitions must stay structurally identical.
 */

import { createContext, useContext, type ReactNode } from 'react';

export interface AtomDefaults {
  defaultVariant?: string;
  defaultSize?: string;
  defaultRadius?: string;
  defaultShadow?: string;
  defaultPadding?: string;
  availableVariants?: string[];
  availableSizes?: string[];
}

export type AtomDefaultsMap = Record<string, AtomDefaults>;

const AtomDefaultsContext = createContext<AtomDefaultsMap>({});

export interface AtomDefaultsProviderProps {
  value: AtomDefaultsMap;
  children: ReactNode;
}

export function AtomDefaultsProvider({ value, children }: AtomDefaultsProviderProps) {
  return <AtomDefaultsContext.Provider value={value}>{children}</AtomDefaultsContext.Provider>;
}

export function useAtomDefaults(atomType: string): AtomDefaults {
  const map = useContext(AtomDefaultsContext);
  return map[atomType] ?? {};
}
